import { Share2, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useIngestUrlMutation } from '@/client/api/marketing/radar';
import { getApiErrorMessage } from '@/client/utils/errors';
import { MediaPageShell } from './components/MediaPageShell';
import { SocialCoverageView } from './components/SocialCoverageView';
import { useCoverageItems, publishedRangeParams } from './coverage-api';
import { formatCompact } from './fixtures';
import { publicMetrics } from '@/client/types/media';

const SocialPostsPage = () => {
  const { items: allItems } = useCoverageItems({
    channel: 'social',
    ...publishedRangeParams(90),
  });
  const ingest = useIngestUrlMutation();
  const items = allItems.filter((i) => i.kind === 'post');
  const totalReach = items.reduce((s, i) => {
    const m = publicMetrics(i);
    return s + (m.reach ?? m.views ?? 0);
  }, 0);
  const erRates = items
    .map((i) => publicMetrics(i).engagementRate)
    .filter((n): n is number => n != null);
  const avgEr = erRates.length === 0 ? 0 : erRates.reduce((s, n) => s + n, 0) / erRates.length;

  return (
    <MediaPageShell
      title="Social mentions"
      description="Earned, competitor, and tracked social posts — listening archive, not the Studio publish queue."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Coverage' },
        { label: 'Social mentions' },
      ]}
      icon={Share2}
      actions={
        <Button
          type="button"
          className="gap-2"
          disabled={ingest.isPending}
          onClick={() => {
            // The escape hatch for what automation missed: paste the URL and the
            // server fetches, extracts and dedupes it on the canonical form.
            const url = window.prompt('Paste the post URL to add to coverage');
            if (!url?.trim()) return;
            ingest.mutate(
              { url: url.trim(), channel: 'social', kind: 'post' },
              {
                onSuccess: () => toast.success('Added to coverage'),
                onError: (err) =>
                  toast.error(getApiErrorMessage(err, 'Could not add that URL')),
              },
            );
          }}
        >
          <Plus className="h-4 w-4" /> {ingest.isPending ? 'Adding…' : 'Add post'}
        </Button>
      }
      stats={[
        { label: 'Posts tracked', value: String(items.length) },
        { label: 'Combined reach', value: formatCompact(totalReach) },
        { label: 'Avg eng. rate', value: `${(avgEr * 100).toFixed(1)}%` },
        {
          label: 'Platforms',
          value: String(new Set(items.map((i) => i.platform).filter(Boolean)).size),
        },
      ]}
    >
      <p className="mb-4 text-sm text-gray-600">
        To create or schedule owned posts, use{' '}
        <Link
          to="/admin/marketing/studio/social"
          className="font-medium text-blue-700 hover:underline"
        >
          Studio → Distribute → Social queue
        </Link>
        . For keyword mentions, see{' '}
        <Link to="/admin/marketing/radar/inbox?tab=mentions" className="font-medium text-blue-700 hover:underline">
          Inbox → Mentions
        </Link>
        .
      </p>
      <SocialCoverageView kind="post" defaultStatus="all" />
    </MediaPageShell>
  );
};

export default SocialPostsPage;
